import type { Stroke, Point } from '../../types';
import type { HandlePosition, TextStyle } from '../diagram/state/types';
import type { Shape } from '../diagram/shapes/Shape';
import type { Connector } from '../diagram/shapes/Connector';
import type { TextElement } from '../diagram/shapes/TextElement';
import type { DrawShape } from '../draw/shapes';
import type { UnifiedStore } from './UnifiedStore';
import { getStrokeAtPoint } from '../draw/strokeHitTest';
import { getStrokeHandleAtPoint } from '../draw/strokeResize';
import { FONT_FAMILIES } from '../../config/constants';

type AnyElement = Shape | Connector | TextElement | DrawShape;
type HitResult =
  | { kind: 'diagramShape'; element: Shape }
  | { kind: 'connector'; element: Connector }
  | { kind: 'text'; element: TextElement }
  | { kind: 'drawShape'; element: DrawShape }
  | { kind: 'stroke'; stroke: Stroke };

export interface SelectDownResult {
  handled: boolean;
  dragElement: AnyElement | Stroke | null;
  resizeHandle: HandlePosition | null;
  hitKind: HitResult['kind'] | null;
}

interface UnifiedSelectionContext {
  store: UnifiedStore;
  ctx: CanvasRenderingContext2D;
  getInverted: () => boolean;
  getScale: () => number;
  render: () => void;
}

export class UnifiedSelectionManager {
  private readonly context: UnifiedSelectionContext;
  private onSelectionChangeCallback: ((hasSelection: boolean, textStyle: TextStyle | null) => void) | null = null;
  private onConnectorSelectionCallback: ((connector: Connector | null) => void) | null = null;

  // Box selection state
  private isSelectionBox = false;
  private selectionBoxStart: Point | null = null;
  private selectionBoxEnd: Point | null = null;

  constructor(context: UnifiedSelectionContext) {
    this.context = context;
  }

  setOnSelectionChange(callback: (hasSelection: boolean, textStyle: TextStyle | null) => void): void {
    this.onSelectionChangeCallback = callback;
  }

  setOnConnectorSelection(callback: (connector: Connector | null) => void): void {
    this.onConnectorSelectionCallback = callback;
  }

  getElementAtPoint(point: Point): HitResult | null {
    const { store, ctx } = this.context;

    // Text elements (highest z)
    for (let i = store.textElements.length - 1; i >= 0; i--) {
      if (store.textElements[i]!.containsPoint(point, ctx)) {
        return { kind: 'text', element: store.textElements[i]! };
      }
    }

    // Strokes (rendered on top of shapes)
    const strokeHit = getStrokeAtPoint(store.strokes, point);
    if (strokeHit) {
      return { kind: 'stroke', stroke: strokeHit.stroke };
    }

    // Draw shapes
    for (let i = store.drawShapes.length - 1; i >= 0; i--) {
      if (store.drawShapes[i]!.containsPoint(point)) {
        return { kind: 'drawShape', element: store.drawShapes[i]! };
      }
    }

    // Connectors (checked before diagram shapes — they're visually on top in diagram mode)
    for (let i = store.connectors.length - 1; i >= 0; i--) {
      if (store.connectors[i]!.containsPoint(point)) {
        return { kind: 'connector', element: store.connectors[i]! };
      }
    }

    // Diagram shapes
    for (let i = store.shapes.length - 1; i >= 0; i--) {
      if (store.shapes[i]!.containsPoint(point)) {
        return { kind: 'diagramShape', element: store.shapes[i]! };
      }
    }

    return null;
  }

  handleSelectDown(point: Point, shiftKey: boolean): SelectDownResult {
    const { store, ctx } = this.context;
    const selectedIds = store.selectedElements;
    const scale = this.context.getScale();

    // Check resize handles on selected text elements
    for (const textElement of store.textElements) {
      if (selectedIds.has(textElement.id)) {
        const handle = textElement.getHandleAtPoint(point, ctx, scale);
        if (handle) {
          return { handled: true, dragElement: textElement, resizeHandle: handle, hitKind: 'text' };
        }
      }
    }

    // Check resize handles on selected diagram shapes
    for (const shape of store.shapes) {
      if (selectedIds.has(shape.id)) {
        const handle = shape.getHandleAtPoint(point, scale);
        if (handle) {
          return { handled: true, dragElement: shape, resizeHandle: handle, hitKind: 'diagramShape' };
        }
      }
    }

    // Check resize handles on selected connectors
    for (const connector of store.connectors) {
      if (selectedIds.has(connector.id)) {
        const handle = connector.getHandleAtPoint(point, scale);
        if (handle) {
          return { handled: true, dragElement: connector, resizeHandle: handle, hitKind: 'connector' };
        }
      }
    }

    // Check resize handles on selected draw shapes
    for (const drawShape of store.drawShapes) {
      if (selectedIds.has(drawShape.id) && drawShape.selected) {
        const handle = drawShape.getHandleAtPoint(point, scale);
        if (handle) {
          return { handled: true, dragElement: drawShape, resizeHandle: handle, hitKind: 'drawShape' };
        }
      }
    }

    // Check resize handles on selected strokes
    for (const stroke of store.strokes) {
      if (selectedIds.has(stroke.id)) {
        const handle = getStrokeHandleAtPoint(stroke, point, scale);
        if (handle) {
          return { handled: true, dragElement: stroke, resizeHandle: handle, hitKind: 'stroke' };
        }
      }
    }

    // Hit-test all elements
    const hit = this.getElementAtPoint(point);

    if (hit) {
      const element = hit.kind === 'stroke' ? hit.stroke : hit.element;
      const id = element.id;

      if (!shiftKey && !selectedIds.has(id)) {
        this.clearSelection();
      }
      this.selectById(id);
      this.context.render();
      return { handled: true, dragElement: element, resizeHandle: null, hitKind: hit.kind };
    }

    if (!shiftKey) {
      this.clearSelection();
      this.context.render();
    }

    return { handled: false, dragElement: null, resizeHandle: null, hitKind: null };
  }

  selectById(id: string): void {
    const store = this.context.store;

    // Skip if already selected — avoids redundant notifySelectionChange calls
    // that cause toolbar flicker (e.g., during double-click sequences)
    if (store.selectedElements.has(id)) return;

    store.selectedElements.add(id);

    // Set .selected flag on the element
    for (const s of store.shapes) { if (s.id === id) s.selected = true; }
    for (const c of store.connectors) { if (c.id === id) c.selected = true; }
    for (const t of store.textElements) { if (t.id === id) t.selected = true; }
    for (const d of store.drawShapes) { if (d.id === id) d.selected = true; }
    // Strokes don't have a .selected flag; tracked by set membership only.

    this.notifySelectionChange();
  }

  selectElement(element: AnyElement): void {
    this.selectById(element.id);
  }

  clearSelection(): void {
    const store = this.context.store;
    for (const s of store.shapes) s.selected = false;
    for (const c of store.connectors) c.selected = false;
    for (const t of store.textElements) t.selected = false;
    for (const d of store.drawShapes) d.selected = false;
    store.selectedElements.clear();
    this.notifySelectionChange();
  }

  selectAll(): void {
    const store = this.context.store;
    store.selectedElements.clear();
    for (const s of store.shapes) { s.selected = true; store.selectedElements.add(s.id); }
    for (const c of store.connectors) { c.selected = true; store.selectedElements.add(c.id); }
    for (const t of store.textElements) { t.selected = true; store.selectedElements.add(t.id); }
    for (const d of store.drawShapes) { d.selected = true; store.selectedElements.add(d.id); }
    for (const stroke of store.strokes) { store.selectedElements.add(stroke.id); }
    this.notifySelectionChange();
    this.context.render();
  }

  notifySelectionChange(): void {
    const hasSelection = this.context.store.selectedElements.size > 0;
    const textStyle = this.getSelectedTextStyle();
    this.onSelectionChangeCallback?.(hasSelection, textStyle);
    this.onConnectorSelectionCallback?.(this.getSelectedConnector());
  }

  getSelectedConnector(): Connector | null {
    const store = this.context.store;
    for (const c of store.connectors) {
      if (store.selectedElements.has(c.id)) return c;
    }
    return null;
  }

  getSelectedTextStyle(): TextStyle | null {
    const store = this.context.store;
    const selectedIds = store.selectedElements;

    for (const t of store.textElements) {
      if (selectedIds.has(t.id)) return { ...t.textStyle };
    }
    for (const s of store.shapes) {
      if (selectedIds.has(s.id)) return { ...s.textStyle };
    }
    for (const c of store.connectors) {
      if (selectedIds.has(c.id)) {
        return {
          fontSize: c.textSize,
          fontWeight: 'normal',
          fontStyle: 'normal',
          textAlign: 'center',
          fontFamily: FONT_FAMILIES.connector,
          color: this.context.getInverted() ? '#1a1a1f' : '#ffffff',
        };
      }
    }

    return null;
  }

  // --- Box selection ---

  startSelectionBox(point: Point): void {
    this.isSelectionBox = true;
    this.selectionBoxStart = point;
    this.selectionBoxEnd = point;
  }

  updateSelectionBox(point: Point): void {
    if (!this.isSelectionBox) return;
    this.selectionBoxEnd = point;
  }

  finishSelectionBox(): boolean {
    if (!this.selectionBoxStart || !this.selectionBoxEnd) return false;

    const store = this.context.store;
    const minX = Math.min(this.selectionBoxStart.x, this.selectionBoxEnd.x);
    const maxX = Math.max(this.selectionBoxStart.x, this.selectionBoxEnd.x);
    const minY = Math.min(this.selectionBoxStart.y, this.selectionBoxEnd.y);
    const maxY = Math.max(this.selectionBoxStart.y, this.selectionBoxEnd.y);
    const box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

    this.clearSelection();

    // Diagram shapes
    for (const shape of store.shapes) {
      const b = shape.getBounds();
      if (b.x + b.width >= minX && b.x <= maxX && b.y + b.height >= minY && b.y <= maxY) {
        this.selectById(shape.id);
      }
    }

    // Connectors
    for (const connector of store.connectors) {
      const startIn = connector.startPoint.x >= minX && connector.startPoint.x <= maxX &&
        connector.startPoint.y >= minY && connector.startPoint.y <= maxY;
      const endIn = connector.endPoint.x >= minX && connector.endPoint.x <= maxX &&
        connector.endPoint.y >= minY && connector.endPoint.y <= maxY;
      if (startIn || endIn) {
        this.selectById(connector.id);
      }
    }

    // Text elements
    for (const text of store.textElements) {
      const b = text.getBounds(this.context.ctx);
      if (b.x + b.width >= minX && b.x <= maxX && b.y + b.height >= minY && b.y <= maxY) {
        this.selectById(text.id);
      }
    }

    // Draw shapes
    for (const drawShape of store.drawShapes) {
      const b = drawShape.getBounds();
      if (b.x + b.width >= minX && b.x <= maxX && b.y + b.height >= minY && b.y <= maxY) {
        this.selectById(drawShape.id);
      }
    }

    // Strokes
    for (const stroke of store.strokes) {
      if (this.strokeIntersectsBox(stroke, box)) {
        store.selectedElements.add(stroke.id);
      }
    }

    this.isSelectionBox = false;
    this.selectionBoxStart = null;
    this.selectionBoxEnd = null;
    this.notifySelectionChange();
    this.context.render();
    return true;
  }

  private strokeIntersectsBox(stroke: Stroke, box: { x: number; y: number; width: number; height: number }): boolean {
    for (const point of stroke.points) {
      if (point.x >= box.x && point.x <= box.x + box.width &&
          point.y >= box.y && point.y <= box.y + box.height) {
        return true;
      }
    }
    return false;
  }

  getSelectionBoxState(): { active: boolean; start: Point | null; end: Point | null } {
    return {
      active: this.isSelectionBox,
      start: this.selectionBoxStart,
      end: this.selectionBoxEnd,
    };
  }

  isSelectionBoxActive(): boolean {
    return this.isSelectionBox;
  }

  // --- Helpers for selected elements ---

  getSelectedStrokes(): Stroke[] {
    const store = this.context.store;
    return store.strokes.filter(s => store.selectedElements.has(s.id));
  }

  getSelectedStrokeIds(): Set<string> {
    const store = this.context.store;
    const ids = new Set<string>();
    for (const s of store.strokes) {
      if (store.selectedElements.has(s.id)) ids.add(s.id);
    }
    return ids;
  }

  getSelectedDrawShapes(): DrawShape[] {
    const store = this.context.store;
    return store.drawShapes.filter(d => store.selectedElements.has(d.id));
  }

  getSelectedDiagramShapes(): Shape[] {
    const store = this.context.store;
    return store.shapes.filter(s => store.selectedElements.has(s.id));
  }

  getSelectedConnectors(): Connector[] {
    const store = this.context.store;
    return store.connectors.filter(c => store.selectedElements.has(c.id));
  }

  getSelectedTextElements(): TextElement[] {
    const store = this.context.store;
    return store.textElements.filter(t => store.selectedElements.has(t.id));
  }
}
